import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import { Star } from 'lucide-react';
import { firebaseService } from '../lib/firebase';
import { loadFirebaseData, saveFirebaseData } from '../utils/firebaseStorage';
import { AppData } from '../utils/storage';
import { toast } from 'sonner';
import { Unsubscribe } from 'firebase/firestore';

interface FirebaseContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  loadData: () => Promise<AppData | null>;
  saveData: (data: AppData) => Promise<void>;
  signIn: () => Promise<User | null>;
  signOut: () => Promise<void>;
  // Family sharing methods
  createFamily: (initialData: AppData) => Promise<string>;
  joinFamily: (inviteCode: string) => Promise<string>;
  generateInviteCode: () => Promise<string>;
  revokeInvite: (inviteCode: string) => Promise<void>;
  getUserFamilyId: () => Promise<string | null>;
  listenToFamilyData: (familyId: string, callback: (data: AppData) => void) => Unsubscribe;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const saveQueueRef = useRef<AppData | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = useCallback(async (): Promise<AppData | null> => {

    if (!isInitialized) {
      return null;
    }

    try {
      const data = await loadFirebaseData();
      return data;
    } catch (error) {
      console.error('[FirebaseProvider.tsx] loadData - Error:', {
        error: error,
        message: (error as Error).message || 'no message',
        stack: (error as Error).stack || 'no stack'
      });
      setError(error instanceof Error ? error.message : 'Unknown error');
      toast.error('Failed to load data');
      return null;
    }
  }, [isInitialized]);

  const saveData = useCallback(async (data: AppData): Promise<void> => {

    if (!isInitialized) {
      return;
    }

    // Queue the save to prevent multiple rapid calls
    saveQueueRef.current = data;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      const dataToSave = saveQueueRef.current;

      if (!dataToSave) {
        return;
      }

      try {
        await saveFirebaseData(dataToSave);
      } catch (error) {
        console.error('[FirebaseProvider.tsx] saveData timeout - Error:', {
          error: error,
          message: (error as Error).message || 'no message',
          stack: (error as Error).stack || 'no stack'
        });
        setError(error instanceof Error ? error.message : 'Unknown error');
        toast.error('Failed to save data');
      }
    }, 500); // Debounce saves by 500ms

  }, [isInitialized]);

  const signIn = useCallback(async (): Promise<User | null> => {
    try {
      setLoading(true);
      const signedInUser = await firebaseService.signInWithGoogle();
      
      setUser(signedInUser);
      setLoading(false);
      
      if (!signedInUser) {
        toast.error('Sign in failed');
      }
      
      return signedInUser;
    } catch (error) {
      console.error('[FirebaseProvider.tsx] signIn - Error:', {
        error: error,
        message: (error as Error).message || 'no message',
        stack: (error as Error).stack || 'no stack'
      });
      setError(error instanceof Error ? error.message : 'Sign in failed');
      setLoading(false);
      toast.error('Sign in failed');
      return null;
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await firebaseService.signOut();
      setUser(null);
    } catch (error) {
      console.error('[FirebaseProvider.tsx] signOut - Error:', {
        error: error,
        message: (error as Error).message || 'no message',
        stack: (error as Error).stack || 'no stack'
      });
      setError(error instanceof Error ? error.message : 'Sign out failed');
      toast.error('Sign out failed');
    }
  }, []);

  useEffect(() => {

    let mounted = true;

    const unsubscribe = firebaseService.getAuth().onAuthStateChanged(
      (user) => {

        if (mounted) {
          setUser(user);
          setLoading(false);
          setError(null);
          setIsInitialized(true);
        }
      },
      (authError) => {
        console.error('[FirebaseProvider.tsx] Auth State Change Error:', {
          error: authError,
          code: (authError as Error & { code?: string }).code || 'no code',
          message: (authError as Error).message || 'no message',
          stack: (authError as Error).stack || 'no stack'
        });
        if (mounted) {
          setError(authError.message);
          setLoading(false);
          setIsInitialized(true);
        }
      }
    );

    return () => {
      mounted = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      unsubscribe();
    };
  }, []);

  // Family sharing methods - MUST be before conditional return to follow React hooks rules
  const createFamily = useCallback(async (initialData: AppData): Promise<string> => {
    try {
      const familyId = await firebaseService.createFamily(initialData);
      toast.success('Family created! Share your invite code with other parents.');
      return familyId;
    } catch (error) {
      console.error('[FirebaseProvider] createFamily - Error:', error);
      toast.error('Failed to create family');
      throw error;
    }
  }, []);

  const joinFamily = useCallback(async (inviteCode: string): Promise<string> => {
    try {
      const familyId = await firebaseService.joinFamily(inviteCode);
      toast.success('Successfully joined family!');
      // Reload data after joining
      await loadData();
      return familyId;
    } catch (error) {
      console.error('[FirebaseProvider] joinFamily - Error:', error);
      toast.error('Failed to join family. Check your invite code.');
      throw error;
    }
  }, [loadData]);

  const generateInviteCode = useCallback(async (): Promise<string> => {
    try {
      const code = await firebaseService.generateInviteCode();
      toast.success(`Invite code generated: ${code}`);
      return code;
    } catch (error) {
      console.error('[FirebaseProvider] generateInviteCode - Error:', error);
      toast.error('Failed to generate invite code');
      throw error;
    }
  }, []);

  const revokeInvite = useCallback(async (inviteCode: string): Promise<void> => {
    try {
      await firebaseService.revokeInvite(inviteCode);
      toast.success('Invite code revoked');
    } catch (error) {
      console.error('[FirebaseProvider] revokeInvite - Error:', error);
      toast.error('Failed to revoke invite');
      throw error;
    }
  }, []);

  const getUserFamilyId = useCallback(async (): Promise<string | null> => {
    try {
      return await firebaseService.getUserFamilyId();
    } catch (error) {
      console.error('[FirebaseProvider] getUserFamilyId - Error:', error);
      return null;
    }
  }, []);

  const listenToFamilyData = useCallback((familyId: string, callback: (data: AppData) => void): Unsubscribe => {
    try {
      console.log('[FirebaseProvider] Setting up family data listener for:', familyId);
      return firebaseService.listenToFamilyData(familyId, (data) => {
        try {
          callback(data);
        } catch (error) {
          console.error('[FirebaseProvider] listenToFamilyData callback error:', error);
        }
      });
    } catch (error) {
      console.error('[FirebaseProvider] listenToFamilyData setup error:', error);
      // Return a no-op unsubscribe function if setup fails
      return () => {};
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    loadData,
    saveData,
    signIn,
    signOut,
    // Family sharing
    createFamily,
    joinFamily,
    generateInviteCode,
    revokeInvite,
    getUserFamilyId,
    listenToFamilyData,
  };

  // Don't render children until Firebase is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 overflow-hidden">
        {/* Background animated elements */}
        <div className="absolute inset-0">
          {/* Floating stars */}
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute animate-float opacity-30"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <Star 
                size={8 + Math.random() * 16} 
                className="text-yellow-400 fill-yellow-400" 
              />
            </div>
          ))}
          
          {/* Animated gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-600 rounded-full blur-xl opacity-20 animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-gradient-to-r from-pink-400 to-yellow-400 rounded-full blur-xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-full blur-xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* Main loading content */}
        <div className="relative z-10 text-center">
          {/* Large animated star */}
          <div className="mb-8 relative">
            <div className="animate-spin-slow">
              <Star size={80} className="text-yellow-400 fill-yellow-400 drop-shadow-lg" />
            </div>
            {/* Sparkle effects around the main star */}
            <div className="absolute inset-0">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-ping"
                  style={{
                    left: `${50 + 40 * Math.cos((i * Math.PI * 2) / 8)}%`,
                    top: `${50 + 40 * Math.sin((i * Math.PI * 2) / 8)}%`,
                    animationDelay: `${i * 0.2}s`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="w-2 h-2 bg-yellow-300 rounded-full" />
                </div>
              ))}
            </div>
          </div>

          {/* Loading text with emoji rotation */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Star Chores
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <span className="text-lg text-gray-600 dark:text-gray-300">Loading your stars</span>
              <div className="animate-bounce" style={{ animationDelay: '0s' }}>🤖</div>
              <div className="animate-bounce" style={{ animationDelay: '0.1s' }}>🦄</div>
              <div className="animate-bounce" style={{ animationDelay: '0.2s' }}>🚀</div>
              <div className="animate-bounce" style={{ animationDelay: '0.3s' }}>🌊</div>
            </div>
            
            {/* Animated loading dots */}
            <div className="flex justify-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>

            {/* Fun loading messages */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                Preparing your adventure...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextType => {
  const context = useContext(FirebaseContext);
  
  if (context === undefined) {
    console.error('[FirebaseProvider.tsx] useFirebase - Context is undefined, provider not found');
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  
  return context;
};
